/**
 * Every user-facing sentence about imported Kinnser chart lines (EMR-1369).
 *
 * Collected here because the wording is settled product copy, not incidental
 * UI text: the interaction-screening notice in particular is a safety
 * statement, and a paraphrase of it on one surface and not another is how two
 * clinicians come away believing different things about the same list.
 *
 * Written in the chart register — roughly twenty words a sentence, one claim
 * each, no decorative transitions, no asides. Tests assert these strings
 * verbatim, so changing one is a deliberate act with a visible diff.
 */

/** Popover body on a raw medication line in the main list. */
export const IMPORTED_MEDICATION_POPOVER_COPY =
  'Imported from External EMR. Run the Importer tool above to convert this record into a Roger medication.';

/** Popover body on a raw allergy line in the main list. */
export const IMPORTED_ALLERGY_POPOVER_COPY =
  'Imported from External EMR. Run the Importer tool above to convert this record into a Roger allergy.';

/**
 * Shown on the medication surface whenever raw lines are present.
 *
 * The load-bearing sentence of this feature. An imported line carries no FDB
 * concept, so drug-drug screening has never seen it — and a clinician reading a
 * combined list would otherwise reasonably assume it had.
 */
export const IMPORTED_MEDICATIONS_NOT_SCREENED_COPY =
  'These imported medications are not checked for interactions. Run the importer tool to include them.';

/** The button that replaces the list with the reconciliation screen. */
export const OPEN_IMPORTER_TOOL_LABEL = 'Open Patient Data Importer Tool';

/**
 * What the discontinued imported medications actually cover.
 *
 * Kinnser's PAST30DAYS flag is tri-state and all three states say something
 * different. "No other medications were stopped" is a claim a thirty-day window
 * cannot support, and a null flag cannot support either answer — so the display
 * says which it has rather than picking one.
 */
export function discontinuedScopeCopy(
  pastThirtyDays: boolean | null | undefined
): string {
  if (pastThirtyDays === true) {
    return 'External EMR sent discontinued medications from the past 30 days only.';
  }
  if (pastThirtyDays === false) {
    return "External EMR sent this patient's full discontinued medication history.";
  }
  return 'External EMR did not say whether the discontinued list covers 30 days or the full history.';
}

/**
 * Kinnser's patient-level No Known Allergies assertion.
 *
 * Its own state, distinct from an empty list: somebody stated this, and an
 * empty list is the absence of a statement rather than one.
 */
export const IMPORTED_NKA_COPY =
  'External EMR records no known allergies for this patient.';

/** The verb that accepts that assertion onto the Roger chart. */
export const CARRY_FORWARD_NKA_LABEL = 'Carry forward to this chart';

/** Shown once the assertion has been accepted, so nobody is asked twice. */
export const IMPORTED_NKA_CARRIED_COPY =
  'A clinician carried this forward. The chart now records no known allergies.';

/**
 * The second verb in the importer tool.
 *
 * "Import", not "carry forward": discontinued medications are imported too
 * and still display, so a carry-forward verb would read as "keep showing
 * this" when the actual question is "make this a Roger record".
 */
export const DONT_IMPORT_LABEL = "Don't import";

/** Where the reason goes, said once, where the clinician types it. */
export const NOT_CARRIED_REASON_HELP =
  'The reason is kept on the imported record for later review.';

/** The 409 toast when another clinician or tab ruled on the record first. */
export const ALREADY_HANDLED_RELOAD_COPY =
  'Someone already handled this record. Reload the page to update the list.';

/**
 * The imported sibling failed to load (2026-08-28 ruling: never silent).
 *
 * A quiet failure here hides the interaction-screening notice with it, so a
 * patient with unreconciled lines reads identically to one with none. The
 * strip names the absence; the second sentence bounds it.
 */
export const IMPORTED_MEDICATIONS_FETCH_FAILED_COPY =
  'Imported External EMR medication records did not load. The list below shows Roger medications only.';

export const IMPORTED_ALLERGIES_FETCH_FAILED_COPY =
  'Imported External EMR allergy records did not load. The list below shows Roger allergies only.';

export const IMPORTED_FETCH_RETRY_LABEL = 'Retry';

/** Toast for a connection-class failure, where a retry is likely to work. */
export const IMPORTED_FETCH_TRANSIENT_TOAST =
  'Connection problem loading imported External EMR records. Retry in a moment.';

/**
 * Asserting No Known Allergies while imported allergy lines await review
 * (2026-08-28 ruling). The two claims contradict, so the clinician settles
 * both in one deliberate act — or backs out.
 */
export const NKA_DISMISS_IMPORTED_TITLE = 'No Known Allergies';

export const NKA_DISMISS_IMPORTED_WARNING =
  'Selecting No Known Allergies dismisses the imported External EMR allergy records below. They will not become Roger allergies.';

export const NKA_DISMISS_IMPORTED_CONFIRM_LABEL = 'Accept and dismiss imported';

/** The stored not-carried reason on each dismissed line. */
export const NKA_DISMISS_IMPORTED_REASON =
  'Superseded by the No Known Allergies assertion';

/**
 * NKA asked for while coded allergies exist. Those are clinician-entered
 * records; removing them is its own deliberate act, never a side effect.
 */
export const NKA_CODED_ALLERGIES_BLOCK_COPY =
  'This patient has recorded allergies. Remove them before selecting No Known Allergies.';

/**
 * The reconcile form's explicit no-amount assertion (2026-08-28 ruling).
 *
 * A blank field is ambiguous — forgot, or absent? The checkbox makes the
 * skip a statement, the same distinction NKA draws against an empty list.
 */
export const AMOUNT_NA_LABEL = 'Amount N/A';

export const AMOUNT_NA_HELP = 'Records this medication without an amount.';

/**
 * Automatic conversion (EMR-2408/2415). A line whose Medi-Span to FDB match is
 * certain becomes a coded record during the import. It is shown as the
 * import's work, never as a clinician's, and it can be reverted.
 */
export const CONVERTED_AUTOMATICALLY_TITLE =
  'Converted automatically from External EMR';

export const CONVERTED_AUTOMATICALLY_BODY =
  'The match was certain, so the import made this record. Revert it if the match is wrong.';

export const CONVERTED_RECORD_REMOVED_COPY =
  'The coded record was removed in Roger. Revert returns this line for review.';

export const REVERT_CONFIRM_COPY =
  'Revert removes the coded record and returns this line for review.';

/** A likely match for a raw line. Never selected until the clinician clicks. */
export const SUGGESTED_FROM_PREVIOUS_EMR_LABEL = 'Suggested from External EMR';

export const SUGGESTED_FROM_PREVIOUS_EMR_HELP =
  'A likely match. Select it to use it, or search for another.';

/** The quiet line that still opens the tool once nothing is left raw. */
export function convertedCountCopy(
  count: number,
  kind: 'medication' | 'allergy'
): string {
  const noun =
    kind === 'medication'
      ? count === 1
        ? 'medication'
        : 'medications'
      : count === 1
        ? 'allergy'
        : 'allergies';
  return `${count} imported ${noun} ${count === 1 ? 'was' : 'were'} coded automatically.`;
}

export const REVIEW_CONVERSIONS_LABEL = 'Review';
